import { MarketplaceSearchRepresentationSchema, MarketplaceSearchAcquisitionSchema } from './marketplace-search-representation';
import { z } from 'zod';
import { MarketplaceDbClaimTokenSchema } from './marketplace-db-claim';
import { RecruiterVerificationRequirementSchema, recruiterVerificationDiagnostic, RECRUITER_CLAUSE_OVERFLOW } from './recruiter-verification';

/** Marketplace Search V2 wire contract. Keep values stable across gateway/RPC/UI. */
export const MARKETPLACE_SEARCH_CONTRACT_VERSION = 2 as const;
export const MARKETPLACE_SEARCH_MODEL_VERSION = 'marketplace-structured-v2' as const;

const boundedText = (max: number) => z.string().trim().min(1).max(max);
const boundedTextArray = (maxItems: number, maxLength: number) =>
  z.array(boundedText(maxLength)).max(maxItems).superRefine((values, ctx) => {
    const normalized = new Set<string>();
    for (const [index, value] of values.entries()) {
      const key = value.toLocaleLowerCase('en-US');
      if (normalized.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index],
          message: 'Duplicate value',
        });
      }
      normalized.add(key);
    }
  });

/** Canonical taxonomy IDs accepted by the aggregate skill-count boundary. */
export const MarketplaceSkillCountTaxonomyIdSchema = boundedText(100).regex(
  /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
  'Canonical skill IDs must be lowercase alphanumeric slugs',
);
export const MarketplaceSkillCountTaxonomyIdsSchema = z
  .array(MarketplaceSkillCountTaxonomyIdSchema)
  .min(1)
  .max(50)
  .superRefine((values, ctx) => {
    const seen = new Set<string>();
    for (const [index, value] of values.entries()) {
      if (seen.has(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index],
          message: 'Duplicate canonical skill ID',
        });
      }
      seen.add(value);
    }
  });

export const MarketplaceSearchSourceSchema = z.enum(['manual', 'job']);

/**
 * One recruiter intent concept. `label` plus `anyOf` are OR alternatives.
 * Separate entries in `requiredSkills` are AND requirements.
 * `canonicalIds` are untrusted transport hints only. Search resolves labels
 * server-side and never treats a browser-supplied ID as serving authority.
 */
export const MarketplaceSkillConceptGroupSchema = z.object({
  conceptId: boundedText(80).optional(),
  label: boundedText(100),
  // 29 alternatives + label preserves the legacy V1 flat-skill maximum of
  // 30 when those ANY searches are migrated into one explicit concept.
  anyOf: boundedTextArray(29, 100).optional(),
  canonicalIds: boundedTextArray(12, 100).optional(),
}).strict().superRefine((group, ctx) => {
  const terms = [group.label, ...(group.anyOf ?? [])];
  if (new Set(terms.map((term) => term.toLocaleLowerCase('en-US'))).size !== terms.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['anyOf'],
      message: 'label and anyOf must be unique within a concept group',
    });
  }
});

export const MarketplaceRoleIntentSchema = z.object({
  title: boundedText(160).optional(),
  canonicalTitle: boundedText(160).optional(),
  family: boundedText(80).optional(),
  familyMode: z.enum(['boost', 'filter']).default('boost'),
}).strict().refine(
  (role) => Boolean(role.title || role.canonicalTitle || role.family),
  { message: 'Role intent must include title, canonicalTitle, or family' },
);

const numberRangeSchema = (maxValue: number) => z.object({
  min: z.number().finite().min(0).max(maxValue).optional(),
  max: z.number().finite().min(0).max(maxValue).optional(),
}).strict().refine(
  (range) => range.min !== undefined || range.max !== undefined,
  { message: 'Range must include min or max' },
).refine(
  (range) => range.min === undefined || range.max === undefined || range.min <= range.max,
  { message: 'Range min must be less than or equal to max' },
);

export const MarketplaceSearchFiltersSchema = z.object({
  experienceYears: numberRangeSchema(80).optional(),
  salaryLpa: numberRangeSchema(10_000).optional(),
  salaryField: z.enum(['current', 'expected', 'either']).default('either'),
  city: boundedText(120).optional(),
  locations: boundedTextArray(20, 120).optional(),
  workMode: z.enum(['remote', 'hybrid', 'onsite']).optional(),
  companies: boundedTextArray(20, 160).optional(),
  education: boundedTextArray(20, 160).optional(),
  noticeDaysMax: z.number().int().min(0).max(365).optional(),
  activeWithinDays: z.number().int().min(1).max(3_650).optional(),
  seniority: boundedTextArray(10, 40).optional(),
  industry: boundedTextArray(10, 100).optional(),
}).strict();

export const MarketplaceSearchPolicyDimensionSchema = z.enum([
  'role',
  'seniority',
  'skills',
  'company',
  'current_location',
  'preferred_location',
  'experience',
  'education',
  'industry',
  'salary_band',
  'notice_band',
  'recency_band',
  'work_mode',
]);

export const MARKETPLACE_SEARCH_UNIVERSAL_POLICY_DIMENSIONS = [
  'notice_band',
  'recency_band',
  'salary_band',
  'work_mode',
] as const satisfies readonly z.infer<typeof MarketplaceSearchPolicyDimensionSchema>[];

/**
 * Search capability minted by Candidate authority from the tenant's current
 * data-access tier. It is server-owned input: browsers never choose fields.
 */
export const MarketplaceSearchPolicySchema = z.object({
  version: z.literal(1),
  tierFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  dimensions: z.array(MarketplaceSearchPolicyDimensionSchema).min(4).max(13),
}).strict().superRefine((policy, ctx) => {
  for (let index = 0; index < policy.dimensions.length; index += 1) {
    const dimension = policy.dimensions[index]!;
    if (index > 0 && policy.dimensions[index - 1]! >= dimension) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dimensions', index],
        message: 'Policy dimensions must be unique and sorted',
      });
    }
  }
  const dimensions = new Set(policy.dimensions);
  for (const required of MARKETPLACE_SEARCH_UNIVERSAL_POLICY_DIMENSIONS) {
    if (!dimensions.has(required)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dimensions'],
        message: `Policy must include universal ${required} capability`,
      });
    }
  }
});

export const MarketplaceJobReferenceSchema = z.object({
  jobId: z.string().uuid(),
  /** Immutable revision, updated_at, or ETag supplied by the tenant-owned job read. */
  jobVersion: z.union([boundedText(128), z.number().int().nonnegative()]),
}).strict();

export const MarketplaceSearchSpecV2Schema = z.object({
  version: z.literal(MARKETPLACE_SEARCH_CONTRACT_VERSION),
  source: MarketplaceSearchSourceSchema,
  job: MarketplaceJobReferenceSchema.optional(),
  role: MarketplaceRoleIntentSchema.optional(),
  requiredSkills: z.array(MarketplaceSkillConceptGroupSchema).max(30).default([]),
  preferredSkills: z.array(MarketplaceSkillConceptGroupSchema).max(30).default([]),
  excludedSkills: z.array(MarketplaceSkillConceptGroupSchema).max(20).default([]),
  filters: MarketplaceSearchFiltersSchema.default({}),
}).strict().superRefine((spec, ctx) => {
  if (spec.source === 'job' && !spec.job) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['job'],
      message: 'A job source requires jobId and jobVersion',
    });
  }
  if (spec.source === 'manual' && spec.job) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['job'],
      message: 'A manual source cannot include a job reference',
    });
  }
  const totalSkills = spec.requiredSkills.length + spec.preferredSkills.length + spec.excludedSkills.length;
  if (totalSkills > 50) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['requiredSkills'],
      message: 'At most 50 skill concept groups are allowed across all buckets',
    });
  }
  const totalSkillTerms = [
    ...spec.requiredSkills,
    ...spec.preferredSkills,
    ...spec.excludedSkills,
  ].reduce((total, group) => total + 1 + (group.anyOf?.length ?? 0), 0);
  if (totalSkillTerms > 120) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['requiredSkills'],
      message: 'At most 120 total skill terms are allowed across labels and anyOf alternatives',
    });
  }
});

export const MarketplaceSearchSortSchema = z.enum([
  'match',
  'last_active',
  'experience_desc',
  'salary_asc',
  'salary_desc',
]);

/**
 * Minimum title-only role score for a candidate to enter a no-required-skills
 * result set. Family affinity is intentionally not blended into this gate:
 * sharing a broad family must not turn a generic-token title match (for
 * example Java Developer vs JavaScript Developer) into an eligible result.
 */
export const MARKETPLACE_ROLE_TITLE_BASELINE_SCORE = 0.60;

export interface MarketplaceBaselineEligibilitySignals {
  requiredConceptCount: number;
  preferredMatchCount: number;
  hasRoleTitle: boolean;
  roleTitleScore: number | null;
  hasRoleFamily: boolean;
  roleFamilyMatched: boolean;
}

/**
 * Shared no-required-skills eligibility policy used by Search and mirrored by
 * the PostgreSQL serving predicate. Required concepts remain hard-gated by the
 * concept predicate. Otherwise a title must independently clear the title
 * threshold; a family-only role must match its accepted family set; and a
 * preferred-only search must match at least one preferred concept.
 */
export function marketplaceSearchMeetsBaselineEligibility(
  signals: MarketplaceBaselineEligibilitySignals,
): boolean {
  if (signals.requiredConceptCount > 0) return true;
  if (signals.hasRoleTitle) {
    return signals.roleTitleScore !== null
      && signals.roleTitleScore >= MARKETPLACE_ROLE_TITLE_BASELINE_SCORE;
  }
  if (signals.hasRoleFamily) return signals.roleFamilyMatched;
  return signals.preferredMatchCount > 0;
}

export type MarketplaceSearchPolicyDimension = z.infer<typeof MarketplaceSearchPolicyDimensionSchema>;
export type MarketplaceSearchPolicy = z.infer<typeof MarketplaceSearchPolicySchema>;

/** Return every requested dimension that the authority policy does not allow. */
export function marketplaceSearchPolicyMissingDimensionsForSpec(
  spec: z.infer<typeof MarketplaceSearchSpecV2Schema>,
  policy: MarketplaceSearchPolicy,
  sortBy?: z.infer<typeof MarketplaceSearchSortSchema>,
): MarketplaceSearchPolicyDimension[] {
  const requested = new Set<MarketplaceSearchPolicyDimension>();
  if (spec.role) requested.add('role');
  if (
    spec.requiredSkills.length > 0
    || spec.preferredSkills.length > 0
    || spec.excludedSkills.length > 0
  ) requested.add('skills');
  if (spec.filters.seniority?.length) requested.add('seniority');
  if (spec.filters.companies?.length) requested.add('company');
  if (spec.filters.city || spec.filters.locations?.length) {
    const allowed = new Set(policy.dimensions);
    if (!allowed.has('current_location') && !allowed.has('preferred_location')) {
      // A string city predicate is valid when at least one location channel is
      // visible. Search separately gates the SQL clauses to the allowed lanes.
      requested.add('current_location');
    }
  }
  if (spec.filters.experienceYears) requested.add('experience');
  if (spec.filters.education?.length) requested.add('education');
  if (spec.filters.industry?.length) requested.add('industry');
  if (spec.filters.salaryLpa) requested.add('salary_band');
  if (spec.filters.noticeDaysMax !== undefined) requested.add('notice_band');
  if (spec.filters.activeWithinDays !== undefined) requested.add('recency_band');
  if (spec.filters.workMode) requested.add('work_mode');
  if (sortBy === 'experience_desc') requested.add('experience');
  if (sortBy === 'last_active') requested.add('recency_band');
  if (sortBy === 'salary_asc' || sortBy === 'salary_desc') requested.add('salary_band');
  const allowed = new Set(policy.dimensions);
  return Array.from(requested).filter((dimension) => !allowed.has(dimension)).sort();
}

export function marketplaceSearchPolicyAllowsSpec(
  spec: z.infer<typeof MarketplaceSearchSpecV2Schema>,
  policy: MarketplaceSearchPolicy,
  sortBy?: z.infer<typeof MarketplaceSearchSortSchema>,
): boolean {
  return marketplaceSearchPolicyMissingDimensionsForSpec(spec, policy, sortBy).length === 0;
}

/** Fixed privacy lattice for Marketplace salary search (LPA lower bounds). */
export const MARKETPLACE_SALARY_LPA_BAND_LOWER_BOUNDS = [
  0, 3, 5, 8, 10, 15, 20, 25, 30, 40, 50, 75, 100, 150, 250, 500, 1_000,
] as const;
export const MARKETPLACE_SALARY_BAND_MAX_INDEX =
  MARKETPLACE_SALARY_LPA_BAND_LOWER_BOUNDS.length - 1;

export function marketplaceSalaryLpaToBandIndex(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const lpa = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(lpa) || lpa < 0) return null;
  for (let index = MARKETPLACE_SALARY_BAND_MAX_INDEX; index >= 0; index -= 1) {
    if (lpa >= MARKETPLACE_SALARY_LPA_BAND_LOWER_BOUNDS[index]!) return index;
  }
  return 0;
}

export function marketplaceSalaryRangeToBandRange(
  range?: { min?: number; max?: number },
): { min?: number; max?: number } | undefined {
  if (!range) return undefined;
  const min = range.min === undefined ? undefined : marketplaceSalaryLpaToBandIndex(range.min);
  const max = range.max === undefined ? undefined : marketplaceSalaryLpaToBandIndex(range.max);
  return {
    ...(min !== undefined && min !== null ? { min } : {}),
    ...(max !== undefined && max !== null ? { max } : {}),
  };
}

export function marketplaceSalaryBandBounds(index: number): {
  minLpa: number;
  maxLpaExclusive: number | null;
} {
  const safeIndex = Math.max(0, Math.min(Math.trunc(index), MARKETPLACE_SALARY_BAND_MAX_INDEX));
  return {
    minLpa: MARKETPLACE_SALARY_LPA_BAND_LOWER_BOUNDS[safeIndex]!,
    maxLpaExclusive: MARKETPLACE_SALARY_LPA_BAND_LOWER_BOUNDS[safeIndex + 1] ?? null,
  };
}

/** Fixed privacy lattices for non-salary Marketplace serving dimensions. */
export const MARKETPLACE_EXPERIENCE_MONTH_BAND_LOWER_BOUNDS = [
  0, 12, 24, 36, 60, 96, 144, 180, 240,
] as const;
export const MARKETPLACE_NOTICE_DAY_BAND_LOWER_BOUNDS = [0, 1, 16, 31, 61, 91] as const;
export const MARKETPLACE_RECENCY_DAY_BAND_LOWER_BOUNDS = [
  0, 2, 8, 15, 31, 61, 91, 181, 366,
] as const;

function marketplaceValueToBandIndex(
  value: unknown,
  lowerBounds: readonly number[],
): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return null;
  for (let index = lowerBounds.length - 1; index >= 0; index -= 1) {
    if (numeric >= lowerBounds[index]!) return index;
  }
  return 0;
}

function marketplaceRangeToBandRange(
  range: { min?: number; max?: number } | undefined,
  lowerBounds: readonly number[],
  multiplier = 1,
): { min?: number; max?: number } | undefined {
  if (!range) return undefined;
  const min = range.min === undefined
    ? undefined
    : marketplaceValueToBandIndex(range.min * multiplier, lowerBounds);
  const max = range.max === undefined
    ? undefined
    : marketplaceValueToBandIndex(range.max * multiplier, lowerBounds);
  return {
    ...(min !== undefined && min !== null ? { min } : {}),
    ...(max !== undefined && max !== null ? { max } : {}),
  };
}

export function marketplaceExperienceMonthsToBandIndex(value: unknown): number | null {
  return marketplaceValueToBandIndex(value, MARKETPLACE_EXPERIENCE_MONTH_BAND_LOWER_BOUNDS);
}

export function marketplaceExperienceYearsRangeToBandRange(
  range?: { min?: number; max?: number },
): { min?: number; max?: number } | undefined {
  return marketplaceRangeToBandRange(
    range,
    MARKETPLACE_EXPERIENCE_MONTH_BAND_LOWER_BOUNDS,
    12,
  );
}

export function marketplaceNoticeDaysToBandIndex(value: unknown): number | null {
  return marketplaceValueToBandIndex(value, MARKETPLACE_NOTICE_DAY_BAND_LOWER_BOUNDS);
}

export function marketplaceRecencyDaysToBandIndex(value: unknown): number | null {
  return marketplaceValueToBandIndex(value, MARKETPLACE_RECENCY_DAY_BAND_LOWER_BOUNDS);
}

export const MarketplaceSearchRequestContextSchema = z.object({
  tenantId: z.string().uuid(),
  actorUserId: boundedText(128),
  requestId: boundedText(128),
  source: MarketplaceSearchSourceSchema,
}).strict();

/** Strict, bounded output of trusted server-side taxonomy compilation. */
export const MarketplaceCompiledSkillConceptSchema = z.object({
  conceptId: boundedText(80),
  label: boundedText(100),
  bucket: z.enum(['required', 'preferred', 'excluded']),
  canonicalIds: boundedTextArray(32, 100),
  rawTerms: boundedTextArray(32, 100),
  primaryCanonicalId: boundedText(100).nullable(),
  resolution: z.enum(['canonical', 'raw', 'composite']),
  matchMode: z.enum(['any', 'all']).optional(),
  compositeParent: boundedText(100).optional(),
  compositeConstituents: boundedTextArray(12, 100).optional(),
  // One bounded alias set per composite constituent. This preserves all-of
  // semantics for exclusions while still recalling legacy/raw labels.
  compositeAlternatives: z.array(
    boundedTextArray(32, 100).refine((values) => values.length > 0, {
      message: 'A composite constituent alias set cannot be empty',
    }),
  ).max(12).optional(),
}).strict().superRefine((concept, ctx) => {
  if (concept.canonicalIds.length === 0 && concept.rawTerms.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['canonicalIds'],
      message: 'A compiled concept must include canonicalIds or rawTerms',
    });
  }
  if (concept.matchMode === 'all' && concept.canonicalIds.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['matchMode'],
      message: 'An all-of concept requires canonicalIds',
    });
  }
  if (concept.compositeAlternatives) {
    if (
      concept.resolution !== 'composite'
      || concept.matchMode !== 'all'
      || concept.compositeAlternatives.length !== concept.canonicalIds.length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['compositeAlternatives'],
        message: 'Composite alternatives require one alias set per all-of constituent',
      });
    }
  }
});

export const MarketplaceCompiledIntentSchema = z.object({
  required: z.array(MarketplaceCompiledSkillConceptSchema).max(120),
  preferred: z.array(MarketplaceCompiledSkillConceptSchema).max(120),
  excluded: z.array(MarketplaceCompiledSkillConceptSchema).max(120),
  unresolvedSkills: boundedTextArray(120, 100),
  compositeExpansions: z.array(z.object({
    input: boundedText(100),
    constituents: boundedTextArray(12, 100),
  }).strict()).max(30),
}).strict().superRefine((intent, ctx) => {
  const all = [
    ...intent.required,
    ...intent.preferred,
    ...intent.excluded,
  ];
  if (all.length > 120) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['required'],
      message: 'At most 120 compiled concepts are allowed across all buckets',
    });
  }
  for (const [bucket, concepts] of [
    ['required', intent.required],
    ['preferred', intent.preferred],
    ['excluded', intent.excluded],
  ] as const) {
    concepts.forEach((concept, index) => {
      if (concept.bucket !== bucket) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [bucket, index, 'bucket'],
          message: `Compiled concept bucket must be ${bucket}`,
        });
      }
    });
  }
});

export type MarketplaceCompiledSkillConcept = z.infer<typeof MarketplaceCompiledSkillConceptSchema>;
export type MarketplaceCompiledIntent = z.infer<typeof MarketplaceCompiledIntentSchema>;

export const MarketplaceSearchQueryFingerprintSchema = z.string().regex(/^[a-f0-9]{64}$/i);
// Includes the DB-signed MVCC snapshot proof. The proof itself can carry a
// 2 KiB pg_snapshot; the outer Search cursor signs/base64url-encodes it again.
export const MarketplaceSearchCursorSchema = boundedText(8_192);

const MarketplaceSearchPageFields = {
  spec: MarketplaceSearchSpecV2Schema,
  cursor: MarketplaceSearchCursorSchema.optional(),
  queryFingerprint: MarketplaceSearchQueryFingerprintSchema.optional(),
  limit: z.number().int().min(1).max(100).default(25),
  sortBy: MarketplaceSearchSortSchema.default('match'),
} as const;

function validateCursorPair(
  request: { cursor?: string; queryFingerprint?: string },
  ctx: z.RefinementCtx,
): void {
  if (Boolean(request.cursor) !== Boolean(request.queryFingerprint)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: request.cursor ? ['queryFingerprint'] : ['cursor'],
      message: 'cursor and queryFingerprint must be supplied together',
    });
  }
}

/** Browser/gateway request. Authenticated context is injected after parsing. */
export const MarketplaceSearchV2ApiRequestSchema = z.object({
  ...MarketplaceSearchPageFields,
  enrich: z.literal(true).default(true),
}).strict().superRefine((request, ctx) => {
  validateCursorPair(request, ctx);
  if (!marketplaceSearchSpecHasRelevanceCriteria(request.spec)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['spec'],
      message: 'Marketplace search requires a role, required skill, or preferred skill',
    });
  }
});

/** Trusted gateway-to-search-service RPC envelope. */
export const MarketplaceSearchV2RpcRequestSchema = z.object({
  ...MarketplaceSearchPageFields,
  requestContext: MarketplaceSearchRequestContextSchema,
  searchPolicy: MarketplaceSearchPolicySchema,
  compiledIntent: MarketplaceCompiledIntentSchema,
  databaseClaim: MarketplaceDbClaimTokenSchema,
  /** Server-only deadline; never changes eligibility or the cursor fingerprint. */
  deadlineAt: z.number().finite().optional(),
}).strict().superRefine((request, ctx) => {
  validateCursorPair(request, ctx);
  if (!marketplaceSearchSpecHasRelevanceCriteria(request.spec)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['spec'],
      message: 'Marketplace search requires a role, required skill, or preferred skill',
    });
  }
  if (request.requestContext.source !== request.spec.source) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['requestContext', 'source'],
      message: 'requestContext.source must match spec.source',
    });
  }
  const missingDimensions = marketplaceSearchPolicyMissingDimensionsForSpec(
    request.spec,
    request.searchPolicy,
    request.sortBy,
  );
  if (missingDimensions.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['searchPolicy', 'dimensions'],
      message: `Search policy does not allow: ${missingDimensions.join(', ')}`,
    });
  }
});

/** Gateway-to-Search aggregate request; raw array calls are retired. */
export const MarketplaceSkillCountsRpcRequestSchema = z.object({
  skillIds: MarketplaceSkillCountTaxonomyIdsSchema,
  requestContext: MarketplaceSearchRequestContextSchema,
  searchPolicy: MarketplaceSearchPolicySchema,
  databaseClaim: MarketplaceDbClaimTokenSchema,
}).strict().refine(
  (request) => request.searchPolicy.dimensions.includes('skills'),
  { path: ['searchPolicy', 'dimensions'], message: 'Skill counts require the skills search dimension' },
);

export const MarketplaceSearchSpecOverridesSchema = z.object({
  role: MarketplaceRoleIntentSchema.nullable().optional(),
  requiredSkills: z.array(MarketplaceSkillConceptGroupSchema).max(30).optional(),
  preferredSkills: z.array(MarketplaceSkillConceptGroupSchema).max(30).optional(),
  excludedSkills: z.array(MarketplaceSkillConceptGroupSchema).max(20).optional(),
  filters: MarketplaceSearchFiltersSchema.optional(),
  sortBy: MarketplaceSearchSortSchema.optional(),
}).strict();

/** Gateway request for tenant-owned job compilation before calling search RPC. */
export const MarketplaceSearchByJobApiRequestSchema = z.object({
  jobId: z.string().uuid(),
  /** Optional optimistic precondition supplied after the initial job compile. */
  expectedJobVersion: MarketplaceJobReferenceSchema.shape.jobVersion.optional(),
  overrides: MarketplaceSearchSpecOverridesSchema.optional(),
  /** Interpretation runs on the first page only; later pages use the accepted criteria. */
  ai: z.object({
    enabled: z.literal(true),
    refinement: z.string().trim().min(1).max(1_000).optional(),
  }).strict().optional(),
  cursor: MarketplaceSearchCursorSchema.optional(),
  queryFingerprint: MarketplaceSearchQueryFingerprintSchema.optional(),
  limit: z.number().int().min(1).max(100).default(25),
  enrich: z.literal(true).default(true),
}).strict().superRefine((request, ctx) => {
  if (request.ai && request.cursor) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['ai'], message: 'AI interpretation is only available for a new search' });
  }
  if (Boolean(request.cursor) !== Boolean(request.queryFingerprint)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: request.cursor ? ['queryFingerprint'] : ['cursor'],
      message: 'cursor and queryFingerprint must be supplied together',
    });
  }
});

export const MarketplaceSkillMatchEvidenceSchema = z.object({
  conceptId: boundedText(80),
  label: boundedText(100),
  matched: z.boolean(),
}).strict();

export const MarketplaceSearchSkillCoverageSchema = z.object({
  requiredMatchedCount: z.number().int().nonnegative(),
  requiredTotalCount: z.number().int().nonnegative(),
  required: z.array(MarketplaceSkillMatchEvidenceSchema).max(120),
  preferred: z.array(MarketplaceSkillMatchEvidenceSchema).max(120),
}).strict().superRefine((coverage, ctx) => {
  if (
    coverage.requiredMatchedCount > coverage.requiredTotalCount
    || coverage.required.length !== coverage.requiredTotalCount
    || coverage.required.filter((concept) => concept.matched).length !== coverage.requiredMatchedCount
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['requiredMatchedCount'],
      message: 'Required coverage counts must match the represented concepts',
    });
  }
  for (const [field, concepts] of [
    ['required', coverage.required],
    ['preferred', coverage.preferred],
  ] as const) {
    if (new Set(concepts.map((concept) => concept.conceptId)).size !== concepts.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [field],
        message: 'Skill coverage concept IDs must be unique',
      });
    }
  }
});

export const MarketplaceSearchRelevanceLabelSchema = z.enum([
  'excellent',
  'strong',
  'partial',
  'weak',
]);

export const MarketplaceSearchResultV2Schema = z.object({
  candidateId: z.string().uuid(),
  relevanceLabel: MarketplaceSearchRelevanceLabelSchema,
  skillCoverage: MarketplaceSearchSkillCoverageSchema,
}).strict();

function rejectHardClauseViolations(
  result: z.infer<typeof MarketplaceSearchResultV2Schema>,
  ctx: z.RefinementCtx,
): void {
  if (result.skillCoverage.requiredMatchedCount !== result.skillCoverage.requiredTotalCount) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['skillCoverage', 'requiredMatchedCount'],
      message: 'Normal marketplace results cannot miss a required skill concept',
    });
  }
}

/** Result safe to serialize in the normal (non-relaxed) result lane. */
export const MarketplaceSearchNormalResultV2Schema = MarketplaceSearchResultV2Schema
  .superRefine(rejectHardClauseViolations);

/** Internal RPC only: binds public cards to the exact current Search representation. */
export const MarketplaceSearchServiceResultV2Schema = MarketplaceSearchResultV2Schema.extend({
  representation: MarketplaceSearchRepresentationSchema,
}).superRefine(rejectHardClauseViolations);

/**
 * Candidate-authority profile data is the only opaque object permitted on a
 * public result. Keep private Search evidence and exact location/activity
 * signals out even if an RPC implementation drifts and places them inside the
 * otherwise-authoritative `data` bag.
 *
 * Keys are normalized so snake_case/camelCase/casing variants share the same
 * boundary. Candidate fields such as `assessment_score` are intentionally not
 * matched by the exact normalized tokens below.
 */
const MARKETPLACE_SEARCH_PRIVATE_DATA_KEY_TOKENS = new Set([
  'activewithindays',
  'components',
  'coordinates',
  'currentlat',
  'currentlng',
  'distance',
  'distanceinkm',
  'distanceinmeters',
  'distancekm',
  'distancekms',
  'experienceyears',
  'explanation',
  'geolat',
  'geolng',
  'lastactiveat',
  'latitude',
  'longitude',
  'matchedby',
  'matchedcandidateskill',
  'matchexplanation',
  'matchreasons',
  'noticedays',
  'rawcandidateevidence',
  'rawmatch',
  'rawmatchdata',
  'rankingscore',
  'relevancescore',
  'salarylpa',
  'score',
  'scorecomponents',
  'searchscore',
]);

function normalizeMarketplaceResultDataKey(key: string): string {
  return key.replace(/[^a-zA-Z0-9]/g, '').toLocaleLowerCase('en-US');
}

export function isMarketplaceSearchPrivateResultDataKey(key: string): boolean {
  const normalized = normalizeMarketplaceResultDataKey(key);
  return normalized === 'constructor'
    || normalized === 'prototype'
    || normalized === 'proto'
    || MARKETPLACE_SEARCH_PRIVATE_DATA_KEY_TOKENS.has(normalized);
}

/**
 * Central public serializer for Candidate-authority data. Public response
 * schemas transform through this function, so both direct and job-derived V2
 * envelopes remove private keys before leaving the Gateway.
 */
export function sanitizeMarketplaceSearchPublicData(
  data: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(data).filter(([key]) => !isMarketplaceSearchPrivateResultDataKey(key)),
  );
}

export const MarketplaceSearchPublicCandidateDataSchema = z.record(z.unknown())
  .transform(sanitizeMarketplaceSearchPublicData);

/**
 * Public result intentionally omits even the coarse internal relevance label:
 * only query-known skill coverage plus Candidate-authority overlay may leave
 * the Gateway.
 */
export const MarketplaceSearchEnrichedResultV2Schema = z.object({
  candidateId: z.string().uuid(),
  skillCoverage: MarketplaceSearchSkillCoverageSchema,
  rank: z.number().int().nonnegative().optional(),
  source: z.enum(['live', 'snapshot', 'discovery']),
  acquisition: MarketplaceSearchAcquisitionSchema.optional(),
  isStale: z.boolean(),
  data: MarketplaceSearchPublicCandidateDataSchema,
  updateAvailable: z.object({
    fromVersion: z.number().int().nonnegative(),
    toVersion: z.number().int().nonnegative(),
    changedFields: boundedTextArray(100, 160),
  }).strict().nullable(),
  verificationLevel: z.enum(['none', 'email', 'phone', 'kyc', 'bgv']).optional(),
  liveness: z.enum(['active', 'recent', 'passive', 'unknown']).optional(),
}).strict().superRefine((result, ctx) => {
  if (result.skillCoverage.requiredMatchedCount !== result.skillCoverage.requiredTotalCount) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['skillCoverage', 'requiredMatchedCount'],
      message: 'Normal marketplace results cannot miss a required skill concept',
    });
  }
});

export const MarketplaceSearchRelaxationSuggestionSchema = z.object({
  kind: z.enum([
    'move_required_to_preferred',
    'widen_experience',
    'widen_location',
    'remove_exclusion',
  ]),
  conceptId: boundedText(80).optional(),
  message: boundedText(240),
  estimatedEligible: z.number().int().nonnegative().optional(),
}).strict();

export const MarketplaceSearchDiagnosticsSchema = z.object({
  supply: z.object({
    eligible: z.number().int().nonnegative(),
    scored: z.number().int().nonnegative(),
    capped: z.boolean(),
  }).strict(),
  unresolvedSkills: boundedTextArray(120, 100),
  compositeExpansions: z.array(z.object({
    input: boundedText(100),
    constituents: boundedTextArray(12, 100),
  }).strict()).max(50),
  relaxationApplied: z.boolean(),
  relaxationSuggestions: z.array(MarketplaceSearchRelaxationSuggestionSchema).max(10),
}).strict();

export const MarketplaceSearchErrorSchema = z.object({
  code: z.enum([
    'INVALID_REQUEST',
    'INVALID_CURSOR',
    'SEARCH_FAILED',
    'SEARCH_TIMEOUT',
    'INTENT_REVIEW_REQUIRED',
    'SERVICE_UNAVAILABLE',
    'MARKETPLACE_UNAVAILABLE',
    'MARKETPLACE_MAINTENANCE',
    'RATE_LIMIT_EXCEEDED',
    'PAYLOAD_TOO_LARGE',
    'JOB_NOT_FOUND',
    'JOB_NOT_SEARCHABLE',
    'JOB_VERSION_STALE',
    'JOB_INTENT_TOO_COMPLEX',
    'FILTER_NOT_AVAILABLE_FOR_TIER',
  ]),
  message: boundedText(500),
  /** Safe client admission hint; serving dependencies/timeouts may be retried. */
  retryable: z.boolean().optional(),
  issues: z.array(boundedText(240)).max(20).optional(),
}).strict();

const MarketplaceSearchResponseFields = {
  version: z.literal(MARKETPLACE_SEARCH_CONTRACT_VERSION),
  modelVersion: boundedText(80),
  success: z.boolean(),
  interpretedSpec: MarketplaceSearchSpecV2Schema.nullable(),
  nextCursor: MarketplaceSearchCursorSchema.nullable(),
  queryFingerprint: MarketplaceSearchQueryFingerprintSchema.nullable(),
  diagnostics: MarketplaceSearchDiagnosticsSchema.nullable(),
  durationMs: z.number().finite().nonnegative(),
  error: MarketplaceSearchErrorSchema.optional(),
} as const;

const MarketplaceSearchServiceV2ResponseBaseSchema = z.object({
  ...MarketplaceSearchResponseFields,
  results: z.array(MarketplaceSearchServiceResultV2Schema).max(100),
}).strict();

const MarketplaceSearchV2ResponseBaseSchema = z.object({
  ...MarketplaceSearchResponseFields,
  results: z.array(MarketplaceSearchEnrichedResultV2Schema).max(100),
}).strict();

function requireConsistentResponseEnvelope(
  response: {
    success: boolean;
    interpretedSpec: unknown | null;
    results: unknown[];
    nextCursor: string | null;
    queryFingerprint: string | null;
    diagnostics: unknown | null;
    error?: unknown;
  },
  ctx: z.RefinementCtx,
): void {
  if (response.success) {
    for (const [field, value] of [
      ['interpretedSpec', response.interpretedSpec],
      ['queryFingerprint', response.queryFingerprint],
      ['diagnostics', response.diagnostics],
    ] as const) {
      if (value === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: `Successful marketplace responses require ${field}`,
        });
      }
    }
    if (response.error !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['error'],
        message: 'Successful marketplace responses cannot include an error',
      });
    }
  } else {
    if (response.results.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['results'],
        message: 'Failed marketplace responses cannot include results',
      });
    }
    if (response.error === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['error'],
        message: 'Failed marketplace responses require a typed error',
      });
    }
    if (response.nextCursor !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['nextCursor'],
        message: 'Failed marketplace responses cannot include a next cursor',
      });
    }
  }
  if (response.nextCursor !== null && response.queryFingerprint === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['queryFingerprint'],
      message: 'A next cursor requires its query fingerprint',
    });
  }
}

/** Search-service RPC response before tenant/privacy enrichment. */
export const MarketplaceSearchServiceV2ResponseSchema = MarketplaceSearchServiceV2ResponseBaseSchema
  .superRefine(requireConsistentResponseEnvelope);

/** Gateway/public response after mandatory candidate privacy-overlay enrichment. */
export const MarketplaceSearchV2ResponseSchema = MarketplaceSearchV2ResponseBaseSchema
  .superRefine(requireConsistentResponseEnvelope);

export const MarketplaceSearchRefillDiagnosticsSchema = z.object({
  rounds: z.number().int().nonnegative(),
  scanned: z.number().int().nonnegative(),
  excluded: z.number().int().nonnegative(),
  capped: z.boolean(),
}).strict();

const MarketplaceSearchGatewayV2ResponseBaseSchema = MarketplaceSearchV2ResponseBaseSchema.extend({
  spec: MarketplaceSearchSpecV2Schema.nullable().optional(),
  enriched: z.boolean().optional(),
  refill: MarketplaceSearchRefillDiagnosticsSchema.optional(),
  partial: z.boolean().optional(),
}).strict();

function requireSuccessfulPublicOverlay(
  response: z.infer<typeof MarketplaceSearchGatewayV2ResponseBaseSchema>,
  ctx: z.RefinementCtx,
): void {
  requireConsistentResponseEnvelope(response, ctx);
  if (response.success && response.enriched !== true) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['enriched'],
      message: 'Successful public marketplace responses must be privacy-overlay enriched',
    });
  }
}

/** Actual gateway HTTP envelope, including bounded overlay/refill metadata. */
export const MarketplaceSearchGatewayV2ResponseSchema = MarketplaceSearchGatewayV2ResponseBaseSchema
  .superRefine(requireSuccessfulPublicOverlay);

export const MarketplaceSearchSourceJobSchema = z.object({
  id: z.string().uuid(),
  title: boundedText(160),
  version: z.union([boundedText(128), z.number().int().nonnegative()]),
  status: boundedText(40).optional(),
}).strict();

/** Bounded, tenant-owned text supplied only by Job RPC, never the browser or AI. */
export const MarketplaceJobDescriptionContextSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('available'), text: z.string().trim().min(1).max(16_000) }).strict(),
  z.object({ status: z.literal('empty') }).strict(),
  z.object({ status: z.literal('too_long') }).strict(),
]);

export const MarketplaceSearchJobProvenanceSchema = z.object({
  compiler: z.literal('vhire-job'),
  compilerVersion: z.literal(1),
  requiredSkillsSource: boundedText(80),
  preferredSkillsSource: boundedText(80),
  appliedOverrides: boundedTextArray(10, 80),
  requestId: boundedText(128),
}).strict();

/** AI may propose criteria, but job identity and the final spec remain server-owned. */
export const MarketplaceJobAiInterpretationSchema = z.object({
  version: z.literal(1),
  source: z.enum(['job-description', 'refinement', 'job-description-and-refinement']),
  status: z.enum(['applied', 'ready_with_verification', 'review_required']),
  unresolvedClauses: z.array(boundedText(300)).max(20),
  blockingClauses: z.array(boundedText(300)).max(20).optional(),
  verificationRequirements: z.array(z.object({
    requirement: RecruiterVerificationRequirementSchema,
    sourceJob: MarketplaceJobReferenceSchema,
  }).strict()).max(20).optional(),
  proposedCriteria: MarketplaceSearchSpecV2Schema.optional(),
  proposals: z.array(z.object({
    source: z.enum(['job-description', 'refinement']),
    criteria: MarketplaceSearchSpecV2Schema,
  }).strict()).max(2).optional(),
  parseTimeMs: z.number().int().nonnegative(),
  cacheHit: z.boolean(),
}).strict().superRefine((value, ctx) => {
  const blockers = value.blockingClauses;
  const verification = value.verificationRequirements;
  if (blockers === undefined && verification === undefined && value.status !== 'ready_with_verification') {
    if (value.status === 'applied' && value.unresolvedClauses.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['status'], message: 'Legacy unresolved clauses remain blocking' });
    }
    return;
  }
  if (blockers === undefined || verification === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Verification metadata must be complete' });
    return;
  }
  const expected = blockers.length ? 'review_required' : verification.length ? 'ready_with_verification' : 'applied';
  if (value.status !== expected) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['status'], message: 'Status must agree with pending requirements' });
  if (verification.length && value.source === 'refinement') ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['source'], message: 'Verification duties require a job description' });
  const pending = new Set([...blockers, ...verification.map(({ requirement }) => recruiterVerificationDiagnostic(requirement))]);
  const overflow = blockers.includes(RECRUITER_CLAUSE_OVERFLOW) && value.unresolvedClauses.includes(RECRUITER_CLAUSE_OVERFLOW);
  if ((!overflow && [...pending].some(issue => !value.unresolvedClauses.includes(issue))) || value.unresolvedClauses.some(issue => !pending.has(issue))) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['unresolvedClauses'], message: 'Every pending requirement must remain visible' });
  }
  const ordered = [...verification].sort((a, b) => a.requirement.start - b.requirement.start);
  if (ordered.some((item, index) => index > 0 && item.requirement.start < ordered[index - 1]!.requirement.end)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['verificationRequirements'], message: 'Verification duties cannot overlap' });
  }
});

/** Job endpoint envelope; source job/version stay separate from serving fingerprint. */
export const MarketplaceSearchByJobV2ResponseSchema = MarketplaceSearchGatewayV2ResponseBaseSchema.extend({
  sourceJob: MarketplaceSearchSourceJobSchema.optional(),
  provenance: MarketplaceSearchJobProvenanceSchema.optional(),
  /** Original Job-service criteria, before recruiter edits or AI additions. */
  sourceBaseSpec: MarketplaceSearchSpecV2Schema.optional(),
  aiInterpretation: MarketplaceJobAiInterpretationSchema.optional(),
}).strict().superRefine((response, ctx) => {
  requireSuccessfulPublicOverlay(response, ctx);
  if (response.success && response.aiInterpretation?.status === 'review_required') {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['aiInterpretation'], message: 'Blocking interpretation cannot return candidates' });
  }
  for (const [index, { sourceJob }] of (response.aiInterpretation?.verificationRequirements ?? []).entries()) {
    if (!response.sourceJob || sourceJob.jobId !== response.sourceJob.id
      || String(sourceJob.jobVersion) !== String(response.sourceJob.version)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['aiInterpretation', 'verificationRequirements', index, 'sourceJob'],
        message: 'Verification duties must identify the current source job and version' });
    }
  }
  if (response.success && !response.sourceJob) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['sourceJob'],
      message: 'Successful by-job responses must include source job provenance',
    });
  }
  if (response.success && !response.provenance) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['provenance'],
      message: 'Successful by-job responses must include compiler provenance',
    });
  }
});

/**
 * True when a parsed spec contains an affirmative relevance signal. Hard
 * exclusions and scalar filters may narrow an already meaningful search, but
 * cannot define candidate relevance on their own.
 */
export function marketplaceSearchSpecHasRelevanceCriteria(
  spec: z.infer<typeof MarketplaceSearchSpecV2Schema>,
): boolean {
  if (spec.role) return true;
  return spec.requiredSkills.length > 0 || spec.preferredSkills.length > 0;
}

export type MarketplaceSearchSource = z.infer<typeof MarketplaceSearchSourceSchema>;
export type MarketplaceSkillConceptGroup = z.infer<typeof MarketplaceSkillConceptGroupSchema>;
export type MarketplaceRoleIntent = z.infer<typeof MarketplaceRoleIntentSchema>;
export type MarketplaceSearchFilters = z.infer<typeof MarketplaceSearchFiltersSchema>;
export type MarketplaceJobReference = z.infer<typeof MarketplaceJobReferenceSchema>;
export type MarketplaceSearchSpecV2 = z.infer<typeof MarketplaceSearchSpecV2Schema>;
export type MarketplaceSearchSort = z.infer<typeof MarketplaceSearchSortSchema>;
export type MarketplaceSearchRequestContext = z.infer<typeof MarketplaceSearchRequestContextSchema>;
export type MarketplaceSearchV2ApiRequest = z.infer<typeof MarketplaceSearchV2ApiRequestSchema>;
export type MarketplaceSearchV2RpcRequest = z.infer<typeof MarketplaceSearchV2RpcRequestSchema>;
export type MarketplaceSkillCountsRpcRequest = z.infer<typeof MarketplaceSkillCountsRpcRequestSchema>;
export type MarketplaceSearchSpecOverrides = z.infer<typeof MarketplaceSearchSpecOverridesSchema>;
export type MarketplaceSearchByJobApiRequest = z.infer<typeof MarketplaceSearchByJobApiRequestSchema>;
export type MarketplaceSkillMatchEvidence = z.infer<typeof MarketplaceSkillMatchEvidenceSchema>;
/** @deprecated Use MarketplaceSkillMatchEvidence. */
export type MarketplaceSkillMatchExplanation = MarketplaceSkillMatchEvidence;
export type MarketplaceSearchSkillCoverage = z.infer<typeof MarketplaceSearchSkillCoverageSchema>;
export type MarketplaceSearchRelevanceLabel = z.infer<typeof MarketplaceSearchRelevanceLabelSchema>;
export type MarketplaceSearchResultV2 = z.infer<typeof MarketplaceSearchResultV2Schema>;
export type MarketplaceSearchNormalResultV2 = z.infer<typeof MarketplaceSearchNormalResultV2Schema>;
export type MarketplaceSearchEnrichedResultV2 = z.infer<typeof MarketplaceSearchEnrichedResultV2Schema>;
export type MarketplaceSearchDiagnostics = z.infer<typeof MarketplaceSearchDiagnosticsSchema>;
export type MarketplaceSearchError = z.infer<typeof MarketplaceSearchErrorSchema>;
export type MarketplaceSearchV2Response = z.infer<typeof MarketplaceSearchV2ResponseSchema>;
export type MarketplaceSearchServiceV2Response = z.infer<typeof MarketplaceSearchServiceV2ResponseSchema>;
export type MarketplaceSearchGatewayV2Response = z.infer<typeof MarketplaceSearchGatewayV2ResponseSchema>;
export type MarketplaceSearchByJobV2Response = z.infer<typeof MarketplaceSearchByJobV2ResponseSchema>;
export type MarketplaceSearchSourceJob = z.infer<typeof MarketplaceSearchSourceJobSchema>;
export type MarketplaceSearchJobProvenance = z.infer<typeof MarketplaceSearchJobProvenanceSchema>;
